<template>
  <transition name="fade" mode="out-in" appear>
    <div v-if="!items" class="offset-top-4 offset-bottom-6">
      <VSpinner />
    </div>
    <article v-else id="INTERNAL_CATALOGUE">
      <div class="container-fixed offset-top-4">
        <form @submit.prevent>
          <div class="row">
            <div class="col-l-4 col-s-12">
              <div class="form-input-set">
                <label for="filterServicePortal"><!-- #S696 -->{{ $t('internalcatalogue.service_portal_filter_selection_label') }}</label>
                <select id="filterServicePortal" v-model="servicePortal" class="form-select" @change="resetInfinite">
                  <option :value="null">
                    {{ $t('internalcatalogue.service_portal_filter_show_all_portals') }}
                    <!-- #S697 -->
                  </option>
                  <option v-for="(name, index) in allServicePortals" :key="index" :title="name" :value="name">
                    {{ name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-l-4 col-s-12">
              <div class="form-input-set">
                <label for="filterSearchPattern"><!-- #TBD -->{{ $t('internalcatalogue.search_input_label') }}</label>
                <input
                  id="filterSearchPattern"
                  v-model.lazy="filter"
                  v-debounce="500"
                  class="form-input"
                  :placeholder="$t('internalcatalogue.search_input_placeholder')"
                  :title="$t('internalcatalogue.search_input_title')"
                />
              </div>
            </div>
            <div class="col-l-4 col-s-12">
              <!-- #I55 -->
              <button type="button" class="btn btn-default pull-right" style="margin-top: 28px;" @click="onResetFilter">
                {{ $t('internalcatalogue.filter_reset_button') }}
              </button>
            </div>
          </div>
        </form>
      </div>
      <div class="container-fluid grayed-offset offset-top-4">
        <div class="container-fixed">
          <h3>
            {{ $t('internalcatalogue.subscriptions_headline') }}
            <!-- #S700 -->
          </h3>
        </div>

        <VInternalCatalogueSubscription v-for="item in lazyItems" :key="item.id" v-bind="item" :action.sync="action" @update="onUpdate" />

        <VInfiniteLoading ref="infiniteLoading" :identifier="infiniteId" @infinite="infiniteHandler">
          <template #no-more>
            <VEmpty />
          </template>
          <template #no-results>
            <VEmpty />
          </template>
          <template #spinner>
            <VSpinner />
          </template>
        </VInfiniteLoading>

        <div class="h-ctr">
          <template v-if="!hasNextItems">
            {{ $tc('internalcatalogue.total_amount_subscriptions_information', items.length, { count: lazyItems.length, total: items.length }) }}
          </template>
          <button v-else type="button" class="btn btn-default h-ctr" data-test="load-next" @click="infiniteHandler(null)">
            {{ $t('internalcatalogue.load_next') }}
          </button>
        </div>
      </div>
    </article>
  </transition>
</template>

<script>
import debounce from 'v-debounce';
import VInfiniteLoading from 'vue-infinite-loading';
import TitleMixin from '@/mixins/title';
import VInternalCatalogueSubscription from '@/components/Substore/InternalCatalogue/Subscription';
import Proxy from '@/proxies/SubstoreInternalCatalogueProxy';
import VSpinner from '@/components/Spinner';
import VEmpty from '@/components/Empty';

const itemsPerPage = 5;

export default {
  name: 'InternalCatalogue',

  directives: {
    debounce,
  },

  components: {
    VInternalCatalogueSubscription,
    VInfiniteLoading,
    VSpinner,
    VEmpty,
  },

  mixins: [TitleMixin],

  data() {
    return {
      infiniteId: 1,
      lazyItemsCnt: itemsPerPage,
      servicePortal: null,
      filter: null,
      items: null,
      action: null,
    };
  },

  computed: {
    allServicePortals() {
      if (!this.items) return null;
      return [...new Set(this.items.map(i => i.servicePortal).filter(s => s))].sort((a, b) => a.localeCompare(b));
    },

    filteredItems() {
      let filteredItems = this.items;
      if (this.servicePortal !== null) {
        filteredItems = filteredItems.filter(i => i.servicePortal === this.servicePortal);
      }
      if (this.filter) {
        const pattern = this.filter.toLowerCase().trim();
        filteredItems = filteredItems.filter(i =>
          [i.name, i.description, i.id, i.servicePortal]
            .filter(Boolean) // discards nulls, undefineds, empty strings and zeroes
            .join(', ')
            .toLowerCase()
            .includes(pattern)
        );
      }
      return filteredItems;
    },

    lazyItems() {
      return this.filteredItems.slice(0, this.lazyItemsCnt);
    },

    hasNextItems() {
      return this.lazyItemsCnt < this.filteredItems.length;
    },
  },

  watch: {
    filter: 'resetInfinite',
  },

  async created() {
    const response = await new Proxy().all();
    if (response.head.fault !== null) throw new Error(response.head.fault);

    this.items = response.data.subscriptions;
  },

  methods: {
    infiniteHandler($state) {
      this.lazyItemsCnt += itemsPerPage;

      if ($state) {
        if (this.hasNextItems) {
          $state.loaded();
        } else {
          $state.complete();
        }
      }
    },

    resetInfinite() {
      this.lazyItemsCnt = itemsPerPage;
      this.infiniteId += 1;
    },

    onResetFilter() {
      this.servicePortal = null;
      this.filter = null;
      this.resetInfinite();
    },

    async onUpdate(subscription) {
      const index = this.items.findIndex(i => i.id === subscription.id);
      this.$set(this.items, index, subscription);

      this.action = { id: subscription.id, message: { type: 'SUCCESS' } };
    },
  },
};
</script>
